import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { AccountRecord, useMonitoringHealthQuery, useSearchAccountsQuery } from '../../api/client'
import AccountRow from '../AccountRow'
import AccountsEmptyState from '../AccountsEmptyState'
import FetchTimePeriodDropDown from '../FetchTimePeriodDropDown'
import Pagination from '../Pagination'
import SearchInput from '../SearchInput'
import { isUserAdminState } from '../../store/DomainStore'
import { selectedAccountState, shouldRefetchAccountsState } from '../../store/UIStore'

import mixpanel from 'mixpanel-browser'
import TeamFilter from '../content/search/TeamFilter'
import PlatformDropdown from '../PlatformDropdown'
import Toggle from '../toggles/Toggle'
import {
    decodeIdsQueryParam,
    encodeStringQueryParam,
    KEYWORD_QUERY_PARAM,
    LIMIT_QUERY_PARAM,
    OFFSET_QUERY_PARAM,
    TEAMS_QUERY_PARAM
} from '../../models/QueryParameters'
import {
    dropDownToGraphQLEnum,
    PROFILE_PARAM_NAME,
    SocialMediaServiceFilterOptions
} from '../../models/SocialMediaServiceSearch'

const DEFAULT_LIMIT = 10

export default function AccountList() {
    // If we get here then we want to reset the onboarding state.
    localStorage.setItem('onboarding', 'false')

    const [searchParams, setSearchParams] = useSearchParams()
    const [showAdminFeatures, setShowAdminFeatures] = useState(false)
    /**
     * We treat the URL as state for our query parameters.
     *
     * We should do this for all query parameters. This will mean that deep linkin
     * is possible and refreshing the page takes you back to the same place.
     */

    /**
     * Retrieve all the query parameters from the URL
     */
    const keyword = searchParams.get(KEYWORD_QUERY_PARAM) || ''
    function handleKeywordChange(keyword: string): void {
        const newSearchParams = new URLSearchParams(searchParams.toString())
        newSearchParams.set(KEYWORD_QUERY_PARAM, keyword)
        newSearchParams.set(OFFSET_QUERY_PARAM, String(0))
        newSearchParams.set(LIMIT_QUERY_PARAM, String(DEFAULT_LIMIT))
        setSearchParams(newSearchParams)
    }

    const teamIdsQueryString = searchParams.get(TEAMS_QUERY_PARAM)
    const teamIds = decodeIdsQueryParam(teamIdsQueryString || '')

    const offset = Number(searchParams.get(OFFSET_QUERY_PARAM)) || 0
    const limit = Number(searchParams.get(LIMIT_QUERY_PARAM)) || DEFAULT_LIMIT

    // DC: commented out until authorisation status is properly determined.
    // const authorisationStatusFilterParam = searchParams.get(
    //     AUTH_STATUS_PARAM_NAME
    // ) as keyof typeof AuthorisationStatusFilterOptions
    // let authorisationStatusFilter = dropDownToGraphQLAuthorisationStatusEnum(
    //     authorisationStatusFilterParam
    // )

    const socialMediaServiceParam = searchParams.get(
        PROFILE_PARAM_NAME
    ) as keyof typeof SocialMediaServiceFilterOptions
    let socialMediaService = dropDownToGraphQLEnum(socialMediaServiceParam)
    let socialMediaServices = (socialMediaService && [socialMediaService]) || []

    // Fetch the initial batch of data
    const { data, loading, fetchMore } = useSearchAccountsQuery({
        variables: {
            params: {
                offset,
                limit,
                keyword,
                socialMediaServices: socialMediaServices,
                // authorisationStatus: authorisationStatusFilter,
                teamIds: teamIds
            }
        },
        onError: (error: any) => {
            throw Error(error.message)
        }
    })

    // Is the user an admin?
    const [admin] = useRecoilState(isUserAdminState)

    const [shouldRefetchAccounts, setShouldRefetchAccounts] = useRecoilState(
        shouldRefetchAccountsState
    )
    const setSelectedAccount = useSetRecoilState(selectedAccountState)

    useEffect(() => {
        setSelectedAccount(undefined)
    }, [setSelectedAccount])

    if (shouldRefetchAccounts) {
        fetchMore({
            variables: {
                offset,
                limit,
                keyword,
                socialMediaService
            }
        })
        setShouldRefetchAccounts(false)
    }

    const accounts = data?.searchAccounts?.accounts as Array<AccountRecord>

    /**
     * Monitoring health for the profiles on screen, in one request.
     *
     * Every profile is asked about, not just the unauthorised ones. The
     * backend decides which have a signal to give, and it is the authorised
     * ones that hide the worst state: a profile Arwen is authorised for AND
     * still scraping collects everything twice under two sets of ids, which
     * filtering them out here made invisible.
     *
     * Admin-only data, so it is skipped entirely unless the admin features
     * are on — no wasted query for ordinary users.
     */
    const healthAccountIds = (accounts ?? []).map((account) => account.id)
    const { data: healthData } = useMonitoringHealthQuery({
        variables: { accountIds: healthAccountIds },
        skip: !(admin && showAdminFeatures) || healthAccountIds.length === 0,
        errorPolicy: 'ignore'
    })
    const monitoringHealth = new Map(
        (healthData?.monitoringHealth ?? []).map((signal) => [signal.accountId, signal])
    )
    const total = data?.searchAccounts?.total

    function handleSearch(keyword: string) {
        handleKeywordChange(keyword)
    }

    function generateTable() {
        if (!!!accounts) {
            return null
        }
        return (
            <>
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="border-b border-ink-200 bg-ink-50">
                            <tr>
                                <th
                                    scope="col"
                                    className="px-4 py-2 text-left text-[10.5px] font-semibold uppercase tracking-wide text-ink-500"
                                >
                                    Profile
                                </th>
                                {/* Count columns hide below sm; AccountRow collapses them
                                    into pills inside the profile cell instead. */}
                                <th
                                    scope="col"
                                    className="hidden w-16 px-4 py-2 text-center text-[10.5px] font-semibold uppercase tracking-wide text-ink-500 sm:table-cell"
                                >
                                    Safe
                                </th>
                                <th
                                    scope="col"
                                    className="hidden w-20 px-4 py-2 text-center text-[10.5px] font-semibold uppercase tracking-wide text-ink-500 sm:table-cell"
                                >
                                    Suspect
                                </th>
                                <th
                                    scope="col"
                                    className="hidden w-16 px-4 py-2 text-center text-[10.5px] font-semibold uppercase tracking-wide text-ink-500 sm:table-cell"
                                >
                                    Severe
                                </th>
                                <th scope="col" className="w-32 px-4 py-2">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            className="divide-y divide-ink-100 bg-white"
                            data-cy="account_table_body"
                        >
                            {accounts.map((account) => {
                                return (
                                    <AccountRow
                                        key={account.id}
                                        account={account}
                                        admin={admin}
                                        showAdminFeatures={showAdminFeatures}
                                        monitoringHealth={monitoringHealth.get(account.id)}
                                    />
                                )
                            })}
                        </tbody>
                    </table>
                </div>
                {total && total < DEFAULT_LIMIT ? null : (
                    <div className="border-t border-ink-200 px-4 py-2.5">
                        <Pagination
                            limit={limit}
                            offset={offset}
                            total={total || 0}
                            onChange={(newOffset, newLimit) => {
                                const newSearchParams = new URLSearchParams(searchParams.toString())
                                newSearchParams.set(
                                    OFFSET_QUERY_PARAM,
                                    `${newOffset < 0 ? 0 : newOffset}`
                                )
                                newSearchParams.set(LIMIT_QUERY_PARAM, `${newLimit}`)
                                setSearchParams(newSearchParams)
                            }}
                        />
                    </div>
                )}
            </>
        )
    }

    return (
        <section className="overflow-hidden rounded-lg border border-ink-200 bg-white shadow-sm">
            {/* Card header owns the title and the toolbar, so the filters visibly
                belong to the table they filter. The page title and arwen dot live
                in HomePage's masthead. font-sans + !font-semibold override the
                global Merriweather h1-h4 rule in index.css */}
            <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 px-4 py-2.5">
                <h2 className="mr-2 font-sans !font-semibold text-[13px] text-ink-900">
                    Your profiles
                </h2>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    {admin ? (
                        <div>
                            <TeamFilter
                                teamIds={teamIds}
                                onUpdateSelectedTeams={(teamIds: number[]) => {
                                    mixpanel.track('Search - accounts - team', {
                                        teamIds
                                    })
                                    const newSearchParams = new URLSearchParams(
                                        searchParams.toString()
                                    )
                                    newSearchParams.set(
                                        TEAMS_QUERY_PARAM,
                                        encodeStringQueryParam(teamIds.map((teamId) => `${teamId}`))
                                    )
                                    newSearchParams.set(OFFSET_QUERY_PARAM, String(0))
                                    newSearchParams.set(LIMIT_QUERY_PARAM, String(DEFAULT_LIMIT))
                                    setSearchParams(newSearchParams)
                                }}
                            />
                        </div>
                    ) : null}
                    <div>
                        <PlatformDropdown
                            onChange={() => {
                                // No changes needed here.
                            }}
                        />
                    </div>

                    <div>
                        <FetchTimePeriodDropDown />
                    </div>

                    <div>
                        <button
                            aria-label="Refresh"
                            data-tooltip-content="Click to refresh the page"
                            type="button"
                            className="focus:ring-arwen-500 inline-flex h-9 w-9 items-center justify-center rounded-md border border-ink-200 bg-white text-ink-600 shadow-sm hover:border-ink-300 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
                            onClick={() => {
                                fetchMore({
                                    variables: {
                                        offset: offset,
                                        limit: limit
                                    }
                                })
                            }}
                        >
                            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                    </div>

                    <div className="text-gray-950">
                        <SearchInput
                            keyword={keyword}
                            onKeywordChange={handleKeywordChange}
                            onSearch={handleSearch}
                        />
                    </div>
                    {admin && (
                        <Toggle
                            checked={showAdminFeatures}
                            disabled={false}
                            label="Show admin features"
                            id="showAdminFeatures"
                            loading={false}
                            onToggle={async () => setShowAdminFeatures(!showAdminFeatures)}
                            size="large"
                            tooltip="Click this to show the admin features"
                        ></Toggle>
                    )}
                </div>
            </div>

            {accounts?.length === 0 && (
                <div className="px-4 py-6">
                    <AccountsEmptyState />
                </div>
            )}

            {accounts?.length > 0 && generateTable()}

            {loading && (
                <div>
                    {Array.apply(null, Array(limit)).map((item, index) => {
                        return (
                            <div
                                key={index}
                                className="flex animate-pulse flex-row items-center gap-3 border-b border-ink-100 px-4 py-3 last:border-b-0"
                            >
                                <div className="h-7 w-7 flex-none rounded-md bg-ink-100"></div>
                                <div className="h-4 w-1/3 rounded bg-ink-100"></div>
                                <div className="ml-auto h-4 w-24 rounded bg-ink-100"></div>
                            </div>
                        )
                    })}
                </div>
            )}
        </section>
    )
}
